import Grid from "@/components/Grid";
import ReportListCard from "@/components/ReportListCard";
import { PanelBoxV2 } from "@/tpo/Boxes";
import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

import { AvailableBadge, Badge, ComingSoonBadge, UnlockBadge } from "@/components/reports/Badges";
import { getPatientReportClickDisabled } from "@/components/reports/reportListClick";
import { COMING_SOON } from "@/core/constants";

export default function ReportsGrid({
  query,
  skipQuery,
  queryVariables = {},
  listName = "reports",
  onReportClick,
  userEntitlements = []
}) {
  const theme = useTheme();

  const { data: reportsData } = useQuery(query, {
    variables: {
      ...queryVariables
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: skipQuery
  });

  const reports = reportsData?.[listName];
  const userReports = reportsData?.userReports?.map(userReport => userReport.subsector.id);

  return (
    <PanelBoxV2
      maxWidth={1280}
      outer={{
        bg: "haze",
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: [16, 16, "5.5vw"]
      }}
    >
      <Grid
        gridTemplateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)", "repeat(2, 1fr)"]}
        gap={16}
        gridColumnGap={16}
      >
        {reports?.map((report) => {

          const isComingSoon = report?.purchaseProduct?.status === COMING_SOON;
          const clickDisabled = getPatientReportClickDisabled({
            report,
            userReports: reportsData?.userReports,
            userEntitlements
          });
          const grantedEntitlements = report.purchaseProduct?.grantedEntitlements.map(
            entitlement => entitlement.key
          );
          const isLocked = !userEntitlements?.some(entitlement =>
            grantedEntitlements?.includes(entitlement.key) && entitlement.granted
          );

          let topRightButton = null;
          if (isComingSoon) {
            topRightButton = <ComingSoonBadge />;
          } else if (isLocked) {
            topRightButton = <UnlockBadge />;
          } else if (!userReports.includes(report.id)) {
            topRightButton = <Badge>Awaiting Results</Badge>
          } else {
            topRightButton = <AvailableBadge />;
          }
          
          const userReport = reportsData?.userReports?.find(userReport => userReport.subsector.id == report.id);

          const userReportFunctionalDatapointCount = (userReport?.functionalDatapointCounts?.inRangeCount || 0) + (userReport?.functionalDatapointCounts?.outOfRangeCount || 0);
          const userReportDnaDatapointCount = (userReport?.dnaDatapointCounts?.inRangeCount || 0) + (userReport?.dnaDatapointCounts?.outOfRangeCount || 0);
          const userReportSymptomsCount = (userReport?.questionsAndAnswersCounts?.inRangeCount || 0) + (userReport?.questionsAndAnswersCounts?.outOfRangeCount || 0);

          const totalFunctionalDatapointCount = report?.functionalDatapointCount || 0;
          const totalDnaDatapointCount = report?.dnaDatapointCount || 0;
          const totalSymptomsCount = report?.questionsAndAnswersCount || 0;

          return (
            <ReportListCard
              key={report.id}
              id={report.id}
              name={report.name}
              shortDescription={report?.purchaseProduct?.content?.shortDescription}
              iconType={report?.purchaseProduct?.content?.reportIcon}
              iconSize={[40, 40, 80]}
              onClick={() => {
                if (clickDisabled) return;
                onReportClick?.(report);
              }}
              functionalDatapointCount={totalFunctionalDatapointCount}
              userReportFunctionalDatapointCount={userReportFunctionalDatapointCount}
              dnaDatapointCount={totalDnaDatapointCount}
              userReportDnaDatapointCount={userReportDnaDatapointCount}
              symptomsCount={totalSymptomsCount}
              userReportSymptomsCount={userReportSymptomsCount}
              variant={isComingSoon ? "disabled" : "active"}
              topRightButton={topRightButton}
            />
          );
        })}
      </Grid>
    </PanelBoxV2>
  );
}
